import { combineEpics } from "redux-observable";
import { from } from "rxjs";
import { catchError, concatMap, filter, map, merge, mergeMap } from "rxjs/operators";

import { actionCreators, isOfAction } from "../actions";
import { getMaxRosterRequestSize } from "../reducers/config";
import { getDialogsWithMonolog, getRoster } from "../reducers/roster";
import { notUndefined } from "../utils/any";
import { Epic } from "../utils/types";

// epics

const onGetRoster: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.getRoster)),
        mergeMap(() => {
            const defaultRoster$ = from(App.proto.getRoster({ default: true })).pipe(
                map(data => actionCreators.onRoster({ data })),
                catchError(error => {
                    console.error("Failed to get roster", error);
                    return [actionCreators.getRosterFailed()];
                })
            );
            const userRoster$ = from(App.proto.getUserRoster({ tag: "[user_roster]" })).pipe(
                // skip empty user_roster
                map(data => (data.rooms ? { ...data, rooms: data.rooms } : undefined)),
                filter(notUndefined),
                mergeMap(info => {
                    const ids = info.rooms.map(({ id }) => id);
                    const maxSize = getMaxRosterRequestSize(state$.value);
                    const idsBundles: string[][] = [];
                    for (let i = 0; i < ids.length; i += maxSize) {
                        idsBundles.push(ids.slice(i, i + maxSize));
                    }
                    return from(idsBundles).pipe(
                        concatMap(idsBundle => {
                            return from(
                                App.proto.getRoster(
                                    { room_ids: idsBundle },
                                    { tag: "[roster ids]" }
                                )
                            ).pipe(
                                mergeMap(data => {
                                    const existingIds = (data.rooms || []).map(x => x.id);
                                    const rooms = info.rooms.filter(x =>
                                        existingIds.includes(x.id)
                                    );
                                    return [
                                        actionCreators.onRoster({ data: { ...info, rooms } }),
                                        actionCreators.onRoster({ data }),
                                    ];
                                }),
                                catchError(error => {
                                    console.error("Failed to get roster", error);
                                    return [actionCreators.getRosterFailed()];
                                })
                            );
                        })
                    );
                }),
                catchError(error => {
                    console.error("Failed to get roster", error);
                    return [actionCreators.getRosterFailed()];
                })
            );
            return defaultRoster$.pipe(merge(userRoster$));
        })
    );

const onPerformUsersSearchByName: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.performUsersSearchByName)),
        mergeMap(({ payload }) => {
            const { roomView, value: search, limitToMentionableUsers } = payload;
            const limit = 30;
            const directFilter = { type: "direct" } as const;
            const relaxedFilter = {
                type: "mention",
                relaxed_mention_room_id: roomView.roomId,
            } as const;

            const query = limitToMentionableUsers
                ? search === ""
                    ? { limit, filter: relaxedFilter }
                    : { search, limit, filter: relaxedFilter }
                : { search, limit, filter: directFilter };

            return from(App.proto.getRoster(query, { tag: `[search ${search}]` })).pipe(
                mergeMap(data => {
                    return [
                        actionCreators.onMentionsOrderUpdate({ roomView, data }),
                        actionCreators.onRoster({ data }),
                        actionCreators.onRosterSearchLoaded(roomView),
                    ];
                }),
                merge([actionCreators.onRosterSearchLoading(roomView)]),
                catchError(error => {
                    console.error("Failed to get users by name", payload, error);
                    return [
                        actionCreators.onRosterSearchLoaded(roomView),
                        actionCreators.performUsersSearchByNameFailed(payload),
                    ];
                })
            );
        })
    );

const onPerformUsersSearchByIds: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.performUsersSearchByIds)),
        mergeMap(({ payload }) => {
            const maxSize = getMaxRosterRequestSize(state$.value);
            const { userIds } = payload;
            const idsBundles: string[][] = [];
            for (let i = 0; i < userIds.length; i += maxSize) {
                idsBundles.push(userIds.slice(i, i + maxSize));
            }
            return from(idsBundles).pipe(
                concatMap(idsBundle => {
                    return from(
                        App.proto.getRoster({ user_ids: idsBundle }, { tag: "[user_ids]" })
                    ).pipe(
                        map(data => actionCreators.onRoster({ data })),
                        catchError(error => {
                            console.error("Failed to get users by ids", payload, error);
                            return [actionCreators.performUsersSearchByIdsFailed(payload)];
                        })
                    );
                })
            );
        })
    );

const onEnsureRosterForRoom: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.ensureRosterForRoom)),
        map(({ payload: roomId }) => roomId),
        // skip if room is already known
        filter(roomId => !getRoster(state$.value).has(roomId)),
        mergeMap(roomId =>
            from(App.proto.getRoster({ room_ids: [roomId] }, { tag: "[roster for join]" })).pipe(
                map(data => actionCreators.onRoster({ data })),
                catchError(error => {
                    console.error("Failed to get room info for joined room", roomId, error);
                    return [actionCreators.ensureRosterForRoomFailed(roomId)];
                })
            )
        )
    );

const onEnsureMembersForPrivateRoom: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.ensureMembersForPrivateRoom)),
        mergeMap(({ payload: { roomView } }) => {
            const { roomId } = roomView;
            const room = getRoster(state$.value).get(roomId);
            if (room && room.forum_access && room.forum_access.full) {
                const roomMembersUserIds = room.forum_access.full
                    .map(accessItem =>
                        accessItem !== "everyone" && accessItem.type === "user"
                            ? accessItem.id
                            : undefined
                    )
                    .filter(notUndefined);

                const dialogsWithMonolog = getDialogsWithMonolog(state$.value);
                const unknownMembers = roomMembersUserIds.filter(memberUserId => {
                    const user = dialogsWithMonolog.find(r => {
                        return r.userId === memberUserId;
                    });
                    return !user;
                });

                if (unknownMembers.length) {
                    return [
                        actionCreators.performUsersSearchByIds({
                            roomView,
                            userIds: unknownMembers,
                        }),
                    ];
                }
            }
            return [];
        })
    );

export default combineEpics(
    //
    onGetRoster,
    onPerformUsersSearchByName,
    onPerformUsersSearchByIds,
    onEnsureMembersForPrivateRoom,
    onEnsureRosterForRoom
);
